import React, { useState, useCallback, useEffect, useRef, useLayoutEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useProjects } from '../hooks/useProjects';
import { useKnowledge } from '../hooks/useKnowledge';
import { useCompanies } from '../hooks/useCompanies';
import { useProjectDocuments } from '../hooks/useProjectDocuments';
import { useKnowledgeDocuments } from '../hooks/useKnowledgeDocuments';
import { Favicon } from '../utils/favicon';
import { fileTypeGraphicPathOrGeneric } from '../utils/fileTypeGraphics';
import styles from './ThesconAiScopePanel.module.css';

export interface ThesconAiScopeConfig {
  projectId: string;
  /** Company knowledge bucket (`knowledge/{companyId}/…`; URL `?companyId=`). Independent from project / knowledge collection. */
  companyId: string;
  knowledgeId: string;
  selectedProjectDocumentIds: string[];
  selectedCompanyDocumentIds: string[];
  selectedKnowledgeDocumentIds: string[];
  model: string;
  /** Uses staged retrieval pipeline (analysis/rerank/critic) when enabled. */
  agentMode: boolean;
  /** Agent mode only — supplement retrieval with live web notes (OpenAI search model). */
  webSearch: boolean;
  /** Agent mode only — Anthropic Messages API research pass. */
  claudeQuery: boolean;
  /** Agent mode only — OpenAI Chat Completions research pass (general knowledge). */
  openaiQuery: boolean;
}

const DEFAULT_SCOPE: ThesconAiScopeConfig = {
  projectId: '',
  companyId: '',
  knowledgeId: '',
  selectedProjectDocumentIds: [],
  selectedCompanyDocumentIds: [],
  selectedKnowledgeDocumentIds: [],
  model: 'qwen-14b',
  agentMode: true,
  webSearch: false,
  claudeQuery: false,
  openaiQuery: false,
};

const MODEL_OPTIONS: { value: string; label: string; disabled?: boolean }[] = [
  { value: 'qwen-14b', label: 'Qwen 14B' },
  { value: 'llama-8b', label: 'Llama 8B' },
  { value: 'phi-mini', label: 'Phi Mini' },
  { value: 'critic-7b', label: 'Mistral 7B' },
];

const MODEL_ICON_MAP: Record<string, string> = {
  'qwen-14b': '/assets/graphics/qwen-icon.svg',
  'llama-8b': '/assets/graphics/llama-icon.svg',
  'phi-mini': '/assets/graphics/microsoft-icon.svg',
  'critic-7b': '/assets/graphics/mistral-icon.svg',
};

function getFileIconPath(contentType: string | undefined, filename: string, isDark: boolean): string {
  const ct = (contentType || '').toLowerCase();
  const fn = (filename || '').toLowerCase();
  if (ct.includes('image') || /\.(jpg|jpeg|png|gif|webp)$/.test(fn)) {
    return isDark ? '/assets/icons/document.on.document.svg' : '/assets/icons/document.on.document.black.svg';
  }
  return fileTypeGraphicPathOrGeneric(filename, contentType, isDark);
}

export interface ThesconAiScopePanelHandle {
  close: () => void;
}

interface ThesconAiScopePanelProps {
  config: ThesconAiScopeConfig;
  onChange: (config: ThesconAiScopeConfig) => void;
  disabled?: boolean;
  children?: React.ReactNode;
  documentsWithoutChunks?: string[];
}

const ThesconAiScopePanel = forwardRef<ThesconAiScopePanelHandle, ThesconAiScopePanelProps>(function ThesconAiScopePanel({
  config,
  onChange,
  disabled = false,
  children,
  documentsWithoutChunks = [],
}, ref) {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { t } = useLocale();
  const [isOpen, setIsOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    close: () => setIsOpen(false),
  }), []);
  const [chipsOverflow, setChipsOverflow] = useState(false);
  const chipsRef = useRef<HTMLDivElement>(null);
  const hasAutoSelectedProject = useRef(false);
  const hasAutoSelectedCompany = useRef(false);
  const hasAutoSelectedKnowledge = useRef(false);

  const { projects, loading: projectsLoading } = useProjects();
  const { knowledge, loading: knowledgeLoading } = useKnowledge();
  const { companies, loading: companiesLoading } = useCompanies();

  const { documents: projectDocuments, loading: projectDocsLoading } = useProjectDocuments(
    config.projectId || undefined
  );
  const { documents: companyDocuments, loading: companyDocsLoading } = useKnowledgeDocuments(
    config.companyId || undefined
  );
  /** Same Firestore path as company when ids match — only load the collection once (company section). */
  const knowledgeCollectionId =
    config.knowledgeId && config.knowledgeId !== config.companyId ? config.knowledgeId : undefined;
  const { documents: knowledgeDocuments, loading: knowledgeDocsLoading } = useKnowledgeDocuments(
    knowledgeCollectionId
  );

  const updateConfig = useCallback(
    (updates: Partial<ThesconAiScopeConfig>) => {
      onChange({ ...config, ...updates });
    },
    [config, onChange]
  );

  /** Use documentId for RAG - chunks store documentId, which may differ from Firestore doc id */
  const getDocId = useCallback((d: { id: string; documentId?: string }) => d.documentId || d.id, []);

  // Pre-select all files when project/knowledge is selected and documents load
  useEffect(() => {
    if (!config.projectId) hasAutoSelectedProject.current = false;
    if (
      config.projectId &&
      projectDocuments.length > 0 &&
      config.selectedProjectDocumentIds.length === 0
    ) {
      hasAutoSelectedProject.current = true;
      updateConfig({ selectedProjectDocumentIds: projectDocuments.map(getDocId) });
    }
  }, [config.projectId, config.selectedProjectDocumentIds.length, projectDocuments, updateConfig, getDocId]);

  useEffect(() => {
    if (!config.companyId) hasAutoSelectedCompany.current = false;
    if (
      config.companyId &&
      companyDocuments.length > 0 &&
      config.selectedCompanyDocumentIds.length === 0
    ) {
      hasAutoSelectedCompany.current = true;
      updateConfig({ selectedCompanyDocumentIds: companyDocuments.map(getDocId) });
    }
  }, [config.companyId, config.selectedCompanyDocumentIds.length, companyDocuments, updateConfig, getDocId]);

  useEffect(() => {
    if (!config.knowledgeId) hasAutoSelectedKnowledge.current = false;
    if (
      config.knowledgeId &&
      knowledgeDocuments.length > 0 &&
      config.selectedKnowledgeDocumentIds.length === 0
    ) {
      hasAutoSelectedKnowledge.current = true;
      updateConfig({ selectedKnowledgeDocumentIds: knowledgeDocuments.map(getDocId) });
    }
  }, [config.knowledgeId, config.selectedKnowledgeDocumentIds.length, knowledgeDocuments, updateConfig, getDocId]);

  const companyOptions = useMemo(() => {
    const sorted = companies.map((c) => ({ value: c.id, label: c.name || c.id }));
    const withUnknown =
      config.companyId && !sorted.some((o) => o.value === config.companyId)
        ? [{ value: config.companyId, label: config.companyId }, ...sorted]
        : sorted;
    return [{ value: '', label: t('thescon_ai_scope_no_company') }, ...withUnknown];
  }, [companies, config.companyId, t]);

  /** With a company scope, only that company's projects appear; otherwise all projects. */
  const projectsForCompanyScope = useMemo(() => {
    if (!config.companyId) return projects;
    return projects.filter((p) => p.companyId === config.companyId);
  }, [projects, config.companyId]);

  const projectOptions = useMemo(() => {
    const rows = projectsForCompanyScope.map((p) => ({
      value: p.id,
      label: p.projectName || p.id,
    }));
    return [{ value: '', label: t('thescon_ai_scope_no_project') }, ...rows];
  }, [projectsForCompanyScope, t]);

  // Drop project selection if it does not belong to the selected company (e.g. URL or stale state)
  useEffect(() => {
    if (!config.companyId || !config.projectId) return;
    const p = projects.find((pr) => pr.id === config.projectId);
    if (!p || !p.companyId || p.companyId !== config.companyId) {
      hasAutoSelectedProject.current = false;
      updateConfig({ projectId: '', selectedProjectDocumentIds: [] });
    }
  }, [config.companyId, config.projectId, projects, updateConfig]);

  const knowledgeOptions = [
    { value: '', label: t('thescon_ai_scope_no_knowledge') },
    ...knowledge.map((k) => ({ value: k.id, label: k.knowledgeName || k.id })),
  ];

  const toggleProjectDocument = (docId: string) => {
    const set = new Set(config.selectedProjectDocumentIds);
    if (set.has(docId)) set.delete(docId);
    else set.add(docId);
    updateConfig({ selectedProjectDocumentIds: Array.from(set) });
  };

  const toggleCompanyDocument = (docId: string) => {
    const set = new Set(config.selectedCompanyDocumentIds);
    if (set.has(docId)) set.delete(docId);
    else set.add(docId);
    updateConfig({ selectedCompanyDocumentIds: Array.from(set) });
  };

  const toggleKnowledgeDocument = (docId: string) => {
    const set = new Set(config.selectedKnowledgeDocumentIds);
    if (set.has(docId)) set.delete(docId);
    else set.add(docId);
    updateConfig({ selectedKnowledgeDocumentIds: Array.from(set) });
  };

  const selectAllProjectDocs = () => {
    updateConfig({ selectedProjectDocumentIds: projectDocuments.map(getDocId) });
  };

  const clearProjectDocs = () => {
    updateConfig({ selectedProjectDocumentIds: [] });
  };

  const selectAllCompanyDocs = () => {
    updateConfig({ selectedCompanyDocumentIds: companyDocuments.map(getDocId) });
  };

  const clearCompanyDocs = () => {
    updateConfig({ selectedCompanyDocumentIds: [] });
  };

  const selectAllKnowledgeDocs = () => {
    updateConfig({ selectedKnowledgeDocumentIds: knowledgeDocuments.map(getDocId) });
  };

  const clearKnowledgeDocs = () => {
    updateConfig({ selectedKnowledgeDocumentIds: [] });
  };

  const selectedProject = projects.find((p) => p.id === config.projectId);
  const selectedKnowledge = knowledge.find((k) => k.id === config.knowledgeId);
  const selectedCompanyForScope = config.companyId
    ? companies.find((c) => c.id === config.companyId)
    : undefined;
  const companyScopeName = config.companyId
    ? (selectedCompanyForScope?.name?.trim() || config.companyId)
    : undefined;
  const company = selectedProject?.companyId
    ? companies.find((c) => c.id === selectedProject.companyId)
    : undefined;
  const selectedProjectDocs = projectDocuments.filter((d) =>
    config.selectedProjectDocumentIds.includes(getDocId(d))
  );
  const selectedCompanyDocs = companyDocuments.filter((d) =>
    config.selectedCompanyDocumentIds.includes(getDocId(d))
  );
  const selectedKnowledgeDocs = knowledgeDocuments.filter((d) =>
    config.selectedKnowledgeDocumentIds.includes(getDocId(d))
  );
  const modelLabel = MODEL_OPTIONS.find((m) => m.value === config.model)?.label ?? config.model;

  useLayoutEffect(() => {
    const el = chipsRef.current;
    if (!el) return;
    const check = () => setChipsOverflow(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [
    selectedProject,
    companyScopeName,
    selectedProjectDocs,
    selectedCompanyDocs,
    knowledgeCollectionId,
    selectedKnowledge,
    selectedKnowledgeDocs,
    config.model,
  ]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.inputRow}>
        <div className={styles.configBar}>
          <button
            type="button"
            className={styles.configButton}
            onClick={() => setIsOpen((o) => !o)}
            disabled={disabled}
            aria-expanded={isOpen}
            aria-controls="thescon-ai-scope-panel"
            aria-label={t('thescon_ai_scope_button_label')}
          >
        <img
          src={isDarkMode ? '/assets/icons/slider.horizontal.3.svg' : '/assets/icons/slider.horizontal.3.black.svg'}
          alt=""
          width={14}
          height={14}
          className={styles.configIcon}
        />
            <span>{t('thescon_ai_scope_button_text')}</span>
          </button>
          <div className={styles.previewChipsWrap}>
            <div ref={chipsRef} className={styles.previewChips}>
            {companyScopeName && (
              <div className={styles.previewChip} title={companyScopeName}>
                {selectedCompanyForScope?.website ? (
                  <Favicon websiteUrl={selectedCompanyForScope.website} size={14} retinaMultiplier={2} />
                ) : (
                  <img
                    src={isDarkMode ? '/assets/icons/building.2.svg' : '/assets/icons/building.2.black.svg'}
                    alt=""
                    width={14}
                    height={14}
                  />
                )}
                <span className={styles.previewChipLabel}>{companyScopeName}</span>
              </div>
            )}
            {selectedCompanyDocs.map((doc) => (
              <div key={`c-${doc.id}`} className={styles.previewChip} title={doc.filename}>
                <img
                  src={getFileIconPath(doc.contentType, doc.filename, isDarkMode)}
                  alt=""
                  width={14}
                  height={14}
                />
                <span className={styles.previewChipLabel}>{doc.filename}</span>
              </div>
            ))}
            {selectedProject && (
              <div className={styles.previewChip} title={selectedProject.projectName}>
                {company?.website ? (
                  <Favicon websiteUrl={company.website} size={14} retinaMultiplier={2} />
                ) : (
                  <img
                    src={isDarkMode ? '/assets/icons/building.2.svg' : '/assets/icons/building.2.black.svg'}
                    alt=""
                    width={14}
                    height={14}
                  />
                )}
                <span className={styles.previewChipLabel}>{selectedProject.projectName}</span>
              </div>
            )}
            {selectedProjectDocs.map((doc) => (
              <div key={`p-${doc.id}`} className={styles.previewChip} title={doc.filename}>
                <img
                  src={getFileIconPath(doc.contentType, doc.filename, isDarkMode)}
                  alt=""
                  width={14}
                  height={14}
                />
                <span className={styles.previewChipLabel}>{doc.filename}</span>
              </div>
            ))}
            {selectedKnowledge && knowledgeCollectionId && (
              <div className={styles.previewChip} title={selectedKnowledge.knowledgeName}>
                <img
                  src={isDarkMode ? '/assets/icons/books.vertical.svg' : '/assets/icons/books.vertical.black.svg'}
                  alt=""
                  width={14}
                  height={14}
                />
                <span className={styles.previewChipLabel}>{selectedKnowledge.knowledgeName}</span>
              </div>
            )}
            {selectedKnowledgeDocs.map((doc) => (
              <div key={`k-${doc.id}`} className={styles.previewChip} title={doc.filename}>
                <img
                  src={getFileIconPath(doc.contentType, doc.filename, isDarkMode)}
                  alt=""
                  width={14}
                  height={14}
                />
                <span className={styles.previewChipLabel}>{doc.filename}</span>
              </div>
            ))}
            <div className={styles.previewChip} title={modelLabel}>
              <img
                src={
                  MODEL_ICON_MAP[config.model] ??
                  (isDarkMode ? '/assets/icons/apple.intelligence.svg' : '/assets/icons/apple.intelligence.black.svg')
                }
                alt=""
                width={14}
                height={14}
              />
              <span className={styles.previewChipLabel}>{modelLabel}</span>
            </div>
            </div>
            {chipsOverflow && <span className={styles.previewChipsEllipsis}>…</span>}
          </div>
        </div>
        <div className={styles.formSlot}>{children}</div>
      </div>

      <div
        id="thescon-ai-scope-panel"
        className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`}
        role="region"
        aria-label={t('thescon_ai_scope_panel_label')}
      >
        <div className={styles.panelContent}>
          <div className={styles.panelHeader}>
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => setIsOpen(false)}
              aria-label={t('thescon_ai_scope_close')}
            >
              <img
                src={isDarkMode ? '/assets/icons/xmark.svg' : '/assets/icons/xmark.black.svg'}
                alt=""
                width={12}
                height={12}
              />
            </button>
          </div>
          <div className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label}>{t('thescon_ai_scope_company')}</label>
              <div className={styles.selectWithIcon}>
                {config.companyId ? (
                  <span className={styles.selectLeadingIcon} aria-hidden>
                    {selectedCompanyForScope?.website ? (
                      <Favicon websiteUrl={selectedCompanyForScope.website} size={16} retinaMultiplier={2} />
                    ) : (
                      <img
                        src={isDarkMode ? '/assets/icons/building.2.svg' : '/assets/icons/building.2.black.svg'}
                        alt=""
                        width={16}
                        height={16}
                      />
                    )}
                  </span>
                ) : null}
                <select
                  className={styles.select}
                  value={config.companyId}
                  onChange={(e) => {
                    hasAutoSelectedCompany.current = false;
                    const nextCompanyId = e.target.value;
                    const allowedProjectIds = new Set(
                      (nextCompanyId
                        ? projects.filter((p) => p.companyId === nextCompanyId)
                        : projects
                      ).map((p) => p.id)
                    );
                    const keepProject =
                      Boolean(config.projectId) && allowedProjectIds.has(config.projectId);
                    updateConfig({
                      companyId: nextCompanyId,
                      selectedCompanyDocumentIds: [],
                      ...(!keepProject ? { projectId: '', selectedProjectDocumentIds: [] } : {}),
                    });
                  }}
                  disabled={companiesLoading}
                >
                  {companyOptions.map((o) => (
                    <option key={o.value || '__none'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>{t('thescon_ai_scope_project')}</label>
              <select
                className={styles.select}
                value={config.projectId}
                onChange={(e) => {
                  hasAutoSelectedProject.current = false;
                  const nextProjectId = e.target.value;
                  const proj = nextProjectId
                    ? projects.find((p) => p.id === nextProjectId)
                    : undefined;
                  updateConfig({
                    projectId: nextProjectId,
                    selectedProjectDocumentIds: [],
                    ...(proj?.companyId ? { companyId: proj.companyId } : {}),
                  });
                }}
                disabled={projectsLoading}
              >
                {projectOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>{t('thescon_ai_scope_knowledge')}</label>
              <select
                className={styles.select}
                value={config.knowledgeId}
                onChange={(e) => {
                  hasAutoSelectedKnowledge.current = false;
                  const nextKnowledgeId = e.target.value;
                  updateConfig({
                    knowledgeId: nextKnowledgeId,
                    selectedKnowledgeDocumentIds: [],
                  });
                }}
                disabled={knowledgeLoading}
              >
                {knowledgeOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>{t('thescon_ai_scope_model')}</label>
              <div className={styles.selectWithIcon}>
                <span className={styles.selectLeadingIcon} aria-hidden>
                  <img
                    src={
                      MODEL_ICON_MAP[config.model] ??
                      (isDarkMode ? '/assets/icons/apple.intelligence.svg' : '/assets/icons/apple.intelligence.black.svg')
                    }
                    alt=""
                    width={16}
                    height={16}
                  />
                </span>
                <select
                  className={styles.select}
                  value={config.model}
                  onChange={(e) => updateConfig({ model: e.target.value })}
                >
                  {MODEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} disabled={o.disabled}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Mode</label>
              <select
                className={styles.select}
                value={config.agentMode ? 'agent' : 'standard'}
                onChange={(e) => {
                  const agent = e.target.value === 'agent';
                  updateConfig(
                    agent
                      ? { agentMode: true }
                      : {
                          agentMode: false,
                          webSearch: false,
                          claudeQuery: false,
                          openaiQuery: false,
                        }
                  );
                }}
              >
                <option value="agent">Agent mode (rerank + critic)</option>
                <option value="standard">Standard mode</option>
              </select>
            </div>
          </div>

          {/* Company knowledge documents (below company dropdown) */}
          {config.companyId && (
            <div className={styles.docSection}>
              <div className={styles.docHeader}>
                <span className={styles.docTitle}>{t('thescon_ai_scope_documents_company')}</span>
                {companyDocuments.length > 0 && (
                  <div className={styles.docActions}>
                    <button type="button" className={styles.linkBtn} onClick={selectAllCompanyDocs}>
                      {t('thescon_ai_scope_select_all')}
                    </button>
                    <span className={styles.dot}>·</span>
                    <button type="button" className={styles.linkBtn} onClick={clearCompanyDocs}>
                      {t('thescon_ai_scope_clear')}
                    </button>
                  </div>
                )}
              </div>
              {companyDocsLoading ? (
                <span className={styles.hint}>{t('ask_archive_sheet_loading')}</span>
              ) : companyDocuments.length === 0 ? (
                <span className={styles.hint}>{t('thescon_ai_scope_no_documents')}</span>
              ) : (
                <div className={styles.docList}>
                  {companyDocuments.map((doc) => {
                    const docId = getDocId(doc);
                    const isNotEmbedded = documentsWithoutChunks.includes(doc.id) || documentsWithoutChunks.includes(docId);
                    const docUrl = config.companyId
                      ? `/document/${doc.id}?knowledgeId=${config.companyId}`
                      : `/document/${doc.id}`;
                    return (
                      <label
                        key={doc.id}
                        className={styles.docItem}
                        title={isNotEmbedded ? t('thescon_ai_scope_document_not_embedded') : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={config.selectedCompanyDocumentIds.includes(docId)}
                          onChange={() => toggleCompanyDocument(docId)}
                          className={styles.docCheckbox}
                        />
                        <img
                          src={getFileIconPath(doc.contentType, doc.filename, isDarkMode)}
                          alt=""
                          width={14}
                          height={14}
                          className={styles.docIcon}
                        />
                        <span className={styles.docName}>{doc.filename}</span>
                        <a
                          href={docUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.docOpenLink}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={t('thescon_ai_scope_open_document')}
                          title={t('thescon_ai_scope_open_document')}
                        >
                          <img
                            src={isDarkMode ? '/assets/icons/arrow.up.forward.white.svg' : '/assets/icons/arrow.up.forward.black.svg'}
                            alt=""
                            width={10}
                            height={10}
                          />
                        </a>
                        {isNotEmbedded && (
                          <span className={styles.docNotEmbeddedBadge}>{t('thescon_ai_scope_document_not_embedded')}</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Project documents - always show when project selected */}
          {config.projectId && (
            <div className={styles.docSection}>
              <div className={styles.docHeader}>
                <span className={styles.docTitle}>{t('thescon_ai_scope_documents_project')}</span>
                {projectDocuments.length > 0 && (
                  <div className={styles.docActions}>
                    <button type="button" className={styles.linkBtn} onClick={selectAllProjectDocs}>
                      {t('thescon_ai_scope_select_all')}
                    </button>
                    <span className={styles.dot}>·</span>
                    <button type="button" className={styles.linkBtn} onClick={clearProjectDocs}>
                      {t('thescon_ai_scope_clear')}
                    </button>
                  </div>
                )}
              </div>
              {projectDocsLoading ? (
                <span className={styles.hint}>{t('ask_archive_sheet_loading')}</span>
              ) : projectDocuments.length === 0 ? (
                <span className={styles.hint}>{t('thescon_ai_scope_no_documents')}</span>
              ) : (
                <div className={styles.docList}>
                  {projectDocuments.map((doc) => {
                    const docId = getDocId(doc);
                    const isNotEmbedded = documentsWithoutChunks.includes(doc.id) || documentsWithoutChunks.includes(docId);
                    const docUrl = config.projectId
                      ? `/document/${doc.id}?projectId=${config.projectId}`
                      : `/document/${doc.id}`;
                    return (
                      <label
                        key={doc.id}
                        className={styles.docItem}
                        title={isNotEmbedded ? t('thescon_ai_scope_document_not_embedded') : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={config.selectedProjectDocumentIds.includes(docId)}
                          onChange={() => toggleProjectDocument(docId)}
                          className={styles.docCheckbox}
                        />
                        <img
                          src={getFileIconPath(doc.contentType, doc.filename, isDarkMode)}
                          alt=""
                          width={14}
                          height={14}
                          className={styles.docIcon}
                        />
                        <span className={styles.docName}>{doc.filename}</span>
                        <a
                          href={docUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.docOpenLink}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={t('thescon_ai_scope_open_document')}
                          title={t('thescon_ai_scope_open_document')}
                        >
                          <img
                            src={isDarkMode ? '/assets/icons/arrow.up.forward.white.svg' : '/assets/icons/arrow.up.forward.black.svg'}
                            alt=""
                            width={10}
                            height={10}
                          />
                        </a>
                        {isNotEmbedded && (
                          <span className={styles.docNotEmbeddedBadge}>{t('thescon_ai_scope_document_not_embedded')}</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Knowledge collection documents (hidden when same bucket as company — use company section above) */}
          {knowledgeCollectionId && (
            <div className={styles.docSection}>
              <div className={styles.docHeader}>
                <span className={styles.docTitle}>{t('thescon_ai_scope_documents_knowledge')}</span>
                {knowledgeDocuments.length > 0 && (
                  <div className={styles.docActions}>
                    <button type="button" className={styles.linkBtn} onClick={selectAllKnowledgeDocs}>
                      {t('thescon_ai_scope_select_all')}
                    </button>
                    <span className={styles.dot}>·</span>
                    <button type="button" className={styles.linkBtn} onClick={clearKnowledgeDocs}>
                      {t('thescon_ai_scope_clear')}
                    </button>
                  </div>
                )}
              </div>
              {knowledgeDocsLoading ? (
                <span className={styles.hint}>{t('ask_archive_sheet_loading')}</span>
              ) : knowledgeDocuments.length === 0 ? (
                <span className={styles.hint}>{t('thescon_ai_scope_no_documents')}</span>
              ) : (
                <div className={styles.docList}>
                  {knowledgeDocuments.map((doc) => {
                    const docId = getDocId(doc);
                    const isNotEmbedded = documentsWithoutChunks.includes(doc.id) || documentsWithoutChunks.includes(docId);
                    const docUrl = config.knowledgeId
                      ? `/document/${doc.id}?knowledgeId=${config.knowledgeId}`
                      : `/document/${doc.id}`;
                    return (
                      <label
                        key={doc.id}
                        className={styles.docItem}
                        title={isNotEmbedded ? t('thescon_ai_scope_document_not_embedded') : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={config.selectedKnowledgeDocumentIds.includes(docId)}
                          onChange={() => toggleKnowledgeDocument(docId)}
                          className={styles.docCheckbox}
                        />
                        <img
                          src={getFileIconPath(doc.contentType, doc.filename, isDarkMode)}
                          alt=""
                          width={14}
                          height={14}
                          className={styles.docIcon}
                        />
                        <span className={styles.docName}>{doc.filename}</span>
                        <a
                          href={docUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.docOpenLink}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={t('thescon_ai_scope_open_document')}
                          title={t('thescon_ai_scope_open_document')}
                        >
                          <img
                            src={isDarkMode ? '/assets/icons/arrow.up.forward.white.svg' : '/assets/icons/arrow.up.forward.black.svg'}
                            alt=""
                            width={10}
                            height={10}
                          />
                        </a>
                        {isNotEmbedded && (
                          <span className={styles.docNotEmbeddedBadge}>{t('thescon_ai_scope_document_not_embedded')}</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className={styles.checkRow}>

            {/* Web search */}
            <label className={`${styles.checkItem} ${!config.agentMode ? styles.checkItemMuted : ''}`}>
              <input
                type="checkbox"
                checked={config.agentMode && config.webSearch}
                onChange={(e) => updateConfig({ webSearch: e.target.checked })}
                disabled={disabled || !config.agentMode}
                className={styles.checkInput}
              />
              <span aria-hidden>🌐</span>
              <span>{t('thescon_ai_scope_web_search')}</span>
            </label>

            {/* Claude */}
            <label className={`${styles.checkItem} ${!config.agentMode ? styles.checkItemMuted : ''}`}>
              <input
                type="checkbox"
                checked={config.agentMode && config.claudeQuery}
                onChange={(e) => updateConfig({ claudeQuery: e.target.checked })}
                disabled={disabled || !config.agentMode}
                className={styles.checkInput}
              />
              <img
                src="/assets/graphics/claude-ai-icon.svg"
                alt=""
                width={12}
                height={12}
                className={styles.checkLabelIcon}
              />
              <span>{t('thescon_ai_scope_claude_query')}</span>
            </label>

            {/* OpenAI */}
            <label className={`${styles.checkItem} ${!config.agentMode ? styles.checkItemMuted : ''}`}>
              <input
                type="checkbox"
                checked={config.agentMode && config.openaiQuery}
                onChange={(e) => updateConfig({ openaiQuery: e.target.checked })}
                disabled={disabled || !config.agentMode}
                className={styles.checkInput}
              />
              <img
                src="/assets/graphics/open-ai-icon.svg"
                alt=""
                width={12}
                height={12}
                className={styles.checkLabelIcon}
              />
              <span>{t('thescon_ai_scope_openai_query')}</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ThesconAiScopePanel;
export { DEFAULT_SCOPE };
